import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Heading,
  HStack,
  Image,
  Stack,
  useToast,
  FormControl,
  FormLabel,
  SimpleGrid,
  Tooltip,
  IconButton,
  Button,
  Code,
  Th,
  Tbody,
  Thead,
  Tr,
  Td,
  Table,
  Input,
  NumberInput,
  NumberInputField,
  Badge,
  Text,
  InputGroup,
  InputLeftElement,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";

import {
  HiOutlineUpload,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineSearch,
  HiOutlineRefresh,
  HiOutlinePlay,
  HiOutlineTrash,
  HiOutlineArrowUp,
  HiOutlineArrowDown,
  HiOutlineClipboardCopy,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodesetSelectWithFilters from "./CodesetSelectWithFilters";
import CodesetSelect from "./CodesetSelect";
import { useAuth } from "./AuthContext";
import { withOwnerOrganisation } from "./utils/ownership";
import { errorFromResponse } from "./utils/apiErrors";

/* -----------------------
   Helpers
------------------------*/
const currency = (n) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : n ?? "";

/** Shape a Panner to a clean payload the API expects */
const shapePannerForApi = (p) => {
  const cleanFactorValues = (vals = []) =>
    vals.map((v) => {
      const percent = Number(v.allocationPercent ?? 0);
      const amount = Number(v.allocationAmount ?? 0);
      return {
        id: v.id || crypto.randomUUID(),
        type: v.type ?? "",
        agencyCode: v.agencyCode ?? "",
        agencyCodeSet: v.agencyCodeSet ?? "",
        valueCodeSet: v.valueCodeSet ?? "",
        code: v.code ?? "",
        description: v.description ?? "",
        allocationPercent: isNaN(percent) ? 0 : percent,
        allocationAmount: isNaN(amount) ? 0 : amount,
      };
    });

  const cleanFactors = (factors = []) =>
    factors.map((f) => ({
      id: f.id || crypto.randomUUID(),
      type: f.type ?? "",
      name: f.name ?? "",
      level: Number.isFinite(f.level) ? f.level : 0,
      agencyCode: f.agencyCode ?? "",
      agencyCodeSet: f.agencyCodeSet ?? "",
      allocationType: f.allocationType ?? "",
      isPartial: !!f.isPartial,
      order: Number.isFinite(f.order) ? f.order : 0,
      values: cleanFactorValues(f.values),
    }));

  return {
    _id: p._id,
    name: p.name ?? "",
    definitionId: p.name ?? "", // temporary duplication to keep headers in sync server-side
    premium: Number(p.premium ?? 0),
    placingFactors: cleanFactors(p.placingFactors),
    accountingFactors: cleanFactors(p.accountingFactors),
    projections: p.projections ?? [],
    createdDate: p.createdDate ?? new Date().toISOString(),
    organisationOwnerId: p.organisationOwnerId,
  };
};

/* -----------------------
   Rows & Editors
------------------------*/

/** Single FactorValue row.
 * Props:
 * - fv (object)
 * - parentAgencyCode, parentAgencyCodeSet (from the parent Factor)
 * - premium (number) - the total premium to calculate allocation amount
 * - onChange, onDelete
 */
const FactorValueRow = ({
  fv,
  parentAgencyCode, // from Factor
  parentAgencyCodeSet, // from Factor
  premium, // from top-level panner
  onChange,
  onDelete,
}) => {
  // allow row-level override, else inherit from parent factor
  const effectiveCodeset = fv.agencyCodeSet || parentAgencyCodeSet || "";

  // Calculate allocation amount based on premium and allocation percent
  const allocationAmount = useMemo(() => {
    if (typeof premium !== 'number' || typeof fv.allocationPercent !== 'number') {
      return 0;
    }
    return (premium * fv.allocationPercent) / 100;
  }, [premium, fv.allocationPercent]);

  return (
    <Tr>
      {/* Code (filters to effective Agency Codeset) */}
      <Td>
        {effectiveCodeset ? (
          <CodesetSelect
            codesetCode={effectiveCodeset}
            value={fv.code ? { code: fv.code, description: "" } : null}
            onChange={(val) => onChange({ ...fv, code: val?.code || "" })}
            size="xs"
          />
        ) : (
          <Input
            size="xs"
            isDisabled
            placeholder="Select an agency codeset"
          />
        )}
      </Td>

      <Td>
        <Input
          size="xs"
          value={fv.code ?? ""}
          onChange={e => onChange({ ...fv, code: e.target.value })}></Input>
      </Td>




      <Td isNumeric>
        <NumberInput
          size="xs"
          min="0"
          max="100"
          precision="7"
          value={fv.allocationPercent ?? 0}
          onChange={(_, n) => {
            // Handle NaN values from NumberInput
            const validPercent = isNaN(n) ? 0 : n;
            const newAllocationAmount = isNaN(premium) ? 0 : (premium * validPercent) / 100;
            onChange({
              ...fv,
              allocationPercent: validPercent,
              allocationAmount: newAllocationAmount
            });
          }}>
          <NumberInputField textAlign="right" />
        </NumberInput>
      </Td>

      {/* Allocation Amount (calculated) */}
      <Td isNumeric>
        <Input
          size="xs"
          value={currency(allocationAmount)}
          isReadOnly
          textAlign="right"
          bg="gray.50"
          fontSize="xs"
        />
      </Td>

      {/* Delete */}
      <Td>
        <IconButton
          aria-label="Delete factor value"
          icon={<HiOutlineTrash />}
          size="sm"
          variant="outline"
          onClick={onDelete}
        />
      </Td>
    </Tr>
  );
};

/** Factor editor with minimal fields + nested values */
const FactorEditor = ({ title, factors, setFactors, onCopyToAccounting, premium }) => {
  const toast = useToast();

  const addFactor = () => {
    setFactors([
      ...(factors || []),
      {
        id: crypto.randomUUID(),
        type: "",
        name: "",
        level: 0,
        agencyCode: "",
        agencyCodeSet: "",
        allocationType: "",
        isPartial: false,
        order: (factors?.length || 0) + 1,
        values: [],
      },
    ]);
  };

  const updateFactor = (idx, patch) => {
    const next = [...factors];
    next[idx] = patch;
    setFactors(next);
  };

  const deleteFactor = (idx) => {
    const next = [...factors];
    next.splice(idx, 1);
    setFactors(next);
  };

  const move = (idx, dir) => {
    const next = [...factors];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((f, i) => (next[i] = { ...f, order: i + 1 }));
    setFactors(next);
  };

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={1}
      backgroundColor="whiteAlpha.800"
      h="100%">
      <HStack mb={1}>
        <Heading size="sm">{title}</Heading>
        <Tooltip label="Add factor">
          <IconButton size="xs" icon={<HiOutlinePlus />} onClick={addFactor} />
        </Tooltip>
      </HStack>

      {(factors || []).map((f, i) => (
        <Box
          key={f.id || i}
          borderWidth="2px"
          borderRadius="md"
          borderColor="gray.500"
          p={1}
          mb={1}
          backgroundColor={"white.200"}>
          <HStack mb={1} spacing={2}>

            <Tooltip label="Move factor up">
            <IconButton
              size="xs"
              icon={<HiOutlineArrowUp />}
              onClick={() => move(i, -1)}
              disabled={i === 0}
            />
            </Tooltip>

            <Tooltip label="Move factor down">
            <IconButton
              size="xs"
              icon={<HiOutlineArrowDown />}
              onClick={() => move(i, +1)}
              disabled={i === (factors?.length || 1) - 1}
            />
            </Tooltip>

            <Tooltip label="Add factor">
            <IconButton
              size="xs"
              icon={<HiOutlinePlus />}
              onClick={() =>
                updateFactor(i, {
                  ...f,
                  values: [
                    ...(f.values || []),
                    {
                      id: crypto.randomUUID(),
                      type: f.type || "",
                      agencyCode: f.agencyCode || "",
                      agencyCodeSet: f.agencyCodeSet || "",
                      code: "",
                      allocationPercent: 0,
                      allocationAmount: 0,
                    },
                  ],
                })
              }
            />
            </Tooltip>

<Tooltip label="Copy Placing Factor to Accounting">
  <IconButton
    size="sm"
    aria-label="Copy Placing Factor to Accounting"
    icon={<HiOutlineClipboardCopy />}
    onClick={() => {
      if (!/placing/i.test(title)) {
        toast({ title: "Can only copy from Placing Factors", status: "warning" });
        return;
      }
      if (typeof onCopyToAccounting !== "function") {
        toast({ title: "No Accounting target available", status: "error" });
        return;
      }

      // Build deep copy with fresh ids
      const copy = {
        ...f,
        id: crypto.randomUUID(),
        // let the destination decide final order (we’ll compute there)
        order: 0,
        values: (f.values || []).map(v => ({ ...v, id: crypto.randomUUID() })),
      };

      onCopyToAccounting(copy); // delegate append to parent
      toast({ title: "Copied to Accounting Factors", status: "success" });
    }}
  />
</Tooltip>


          </HStack>
          <SimpleGrid columns={[4]} spacing={2} mb={1}>
            <FormControl>
              <FormLabel mb={1} fontSize="xs" color="gray.600">Type</FormLabel>
              <CodesetSelectWithFilters
                codesetCode="PANNER001"
                size="xs"
                placeholder="Type"
                value={
                  f.type ? { code: String(f.type), description: "" } : null
                } // 👈 object, not string
                onChange={(val) =>
                  updateFactor(i, { ...f, type: val?.code ?? "" })
                } // 👈 unwrap .code
                filters={{ isFactorType: true }}
              />
            </FormControl>
            <FormControl>
              <FormLabel mb={1} fontSize="xs" color="gray.600">Agency</FormLabel>
              <CodesetSelect
                codesetCode="3055"
                filter="#Panner"
                size="xs"
                placeholder="Agency"
                value={
                  f.agencyCode ? { code: f.agencyCode, description: "" } : null
                }
                onChange={(val) => {
                  const agency = val?.code || "";
                  const updatedValues = (f.values || []).map((v) => ({
                    ...v,
                    agencyCode: agency,
                  }));
                  updateFactor(i, {
                    ...f,
                    agencyCode: agency,
                    values: updatedValues,
                  });
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel mb={1} fontSize="xs" color="gray.600">Agency Code Set</FormLabel>
              <CodesetSelect
                codesetCode="C01"
                size="xs"
                placeholder="Agency Codeset"
                value={
                  f.agencyCodeSet
                    ? { code: f.agencyCodeSet, description: "" }
                    : null
                }
                onChange={(val) => {
                  const codeset = val?.code || "";
                  const updatedValues = (f.values || []).map((v) => ({
                    ...v,
                    agencyCodeSet: codeset,
                    code: "", // clear stale code if the codeset changed
                  }));
                  // (optional) also keep agency in sync from the selected codeset object:
                  // const agencyFromSet = val?.codeListAgency || "";
                  // updateFactor(i, { ...f, agencyCode: agencyFromSet, agencyCodeSet: codeset, values: updatedValues });

                  updateFactor(i, {
                    ...f,
                    agencyCodeSet: codeset,
                    values: updatedValues,
                  });
                }}
              />
            </FormControl>

            <FormControl width={"50%"}>
              <FormLabel mb={1} fontSize="xs" color="gray.600">Sum%</FormLabel>
              <Input
                size="xs"
                value={(f.values || []).reduce(
                  (a, b) => a + (b.allocationPercent || 0),
                  0
                )}
                isReadOnly
              />
            </FormControl>
          </SimpleGrid>

          <Table size="xs" variant="striped">
            <Thead>
              <Tr>
                <Th fontSize="xs" color="gray.600">Code</Th>
                <Th fontSize="xs" color="gray.600">Code Value</Th>
                <Th isNumeric fontSize="xs" color="gray.600">Allocation %</Th>
                <Th isNumeric fontSize="xs" color="gray.600">Allocation Amount</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {(f.values || []).map((fv, j) => (
                <FactorValueRow
                  key={fv.id || j}
                  fv={fv}
                  parentAgencyCode={f.agencyCode}
                  parentAgencyCodeSet={f.agencyCodeSet}
                  premium={premium || 0}
                  onChange={(patch) => {
                    const next = [...(f.values || [])];
                    next[j] = patch;
                    updateFactor(i, { ...f, values: next });
                  }}
                  onDelete={() => {
                    const next = [...(f.values || [])];
                    next.splice(j, 1);
                    updateFactor(i, { ...f, values: next });
                  }}
                />
              ))}
            </Tbody>
          </Table>

          <HStack justify="flex-end">
            <IconButton
              icon={<HiOutlineTrash />}
              size="md"
              onClick={() => deleteFactor(i)}
              aria-label="Delete factor"
            />
          </HStack>
        </Box>
      ))}
    </Box>
  );
};

// --- Projections Tabs (shows all projections; newest auto-selected) ---
// --- Projections Tabs (shows all projections; newest auto-selected) ---
const ProjectionsTabs = ({ projections = [], onMapfaculate, lookup }) => {
  const [tab, setTab] = useState(
    projections.length ? projections.length - 1 : 0
  );

  // Jump to newest whenever projections length grows (e.g., after Generate)
  useEffect(() => {
    if (projections.length) setTab(projections.length - 1);
  }, [projections.length]);

  const labelFor = (p, i) => {
    if (p?.type && String(p.type).trim()) return String(p.type);
    return `Projection ${i + 1}`;
  };

  const FactorValueListTable = ({ title, items = [] }) => (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={1}
      backgroundColor="whiteAlpha.700">
      <Heading size="xs" mb={1}>
        {title}
      </Heading>
      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th size="xs"  fontSize="xs" color="gray.600">Key</Th>
            <Th size="xs"  fontSize="xs" color="gray.600">Factor</Th>
            <Th size="xs"  fontSize="xs" color="gray.600">Value</Th>
            <Th isNumeric size="xs" fontSize="xs" color="gray.600">Allocation%</Th>
            <Th isNumeric size="xs"  fontSize="xs" color="gray.600">Amount</Th>
          </Tr>
        </Thead>
        <Tbody>
          {items.length === 0 ? (
            <Tr>
              <Td colSpan={5} color="gray.500" fontStyle="italic">
                No entries
              </Td>
            </Tr>
          ) : (
            items.map((fv) => {
              const factorLabel =
                lookup?.factorLabel(fv.factorId) ||
                lookup?.factorLabel(fv.factor_id) || // in case of different casing
                "";
              const valueLabel =
                fv.valueCode ||
                lookup?.valueLabel(fv.valueId) ||
                lookup?.valueLabel(fv.value_id) ||
                "";
              const allocPct =
                typeof fv.allocationPercent === "number"
                  ? fv.allocationPercent.toFixed(6)
                  : fv.allocationPercent ?? "";
              const allocAmt =
                typeof fv.allocationAmount === "number"
                  ? fv.allocationAmount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : fv.allocationAmount ?? "";
              return (
                <Tr key={fv.id || `${fv.factorId}-${fv.valueId}-${fv.key}`}>
                  <Td fontSize="xs">{fv.key}</Td>
                  <Td fontSize="xs">{factorLabel}</Td>
                  <Td fontSize="xs">{valueLabel}</Td>
                  <Td isNumeric fontSize="xs">{allocPct}</Td>
                  <Td isNumeric fontSize="xs">{allocAmt}</Td>
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={1}
      backgroundColor="whiteAlpha.800">
      <HStack justify="space-between" mb={1}>
        <Heading size="sm">Projections</Heading>
        <Badge>{projections.length}</Badge>
      </HStack>

      {projections.length === 0 ? (
        <Box mt={1} color="gray.600" fontStyle="italic">
          No projections yet — click “Generate combinations”.
        </Box>
      ) : (
        <Tabs
          index={tab}
          onChange={setTab}
          isLazy
          variant="enclosed"
          orientation="vertical"
          size="sm">
          <TabList overflowX="auto" whiteSpace="nowrap">
            {projections.map((p, i) => (
              <Tab key={p.id || p._id || i}>{labelFor(p, i)}</Tab>
            ))}
          </TabList>

          <TabPanels>
            {projections.map((p, i) => {
              const segments = Array.isArray(p.segments) ? p.segments : [];

              return (
                <TabPanel key={p.id || p._id || i} px={0}>
                  <HStack mt={1} mb={1} spacing={2}>
                    <Button
                      variant="outline"
                      leftIcon={<HiOutlinePlay />}
                      onClick={() => onMapfaculate(p.id || p._id)}
                      isDisabled={!(p?.id || p?._id)}>
                      Map factors & allocate (this projection)
                    </Button>
                  </HStack>

                  {segments.length === 0 ? (
                    <Box color="gray.600" fontStyle="italic">
                      This projection has no segments.
                    </Box>
                  ) : (
                    <Stack spacing={3}>
                      {segments.map((s) => {
                        const allocPct =
                          typeof s.allocationPercent === "number"
                            ? s.allocationPercent.toFixed(6)
                            : s.allocationPercent ?? "";
                        const premium =
                          typeof s.premium === "number"
                            ? currency(s.premium)
                            : s.premium ?? "";

                        return (
                          <Box
                            key={s.id}
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            backgroundColor="gray.100">
                            <SimpleGrid
                              columns={{ base: 3, md: 3 }}
                              spacing={3}>
                              <Box
                                borderWidth="1px"
                                borderRadius="md"
                                p={1}
                                backgroundColor="whiteAlpha.700">
                                <Heading size="xs" mb={1}>
                                  Premium Segmentation Map
                                </Heading>
                                <SimpleGrid
                                  columns={{ base: 1, md: 5 }}
                                  spacing={3}
                                  mb={1}>
                                  <Box>
                                    <Text fontSize="xs" color="gray.600">
                                      Allocation %
                                    </Text>
                                    <Text fontSize="xs">{allocPct}</Text>
                                  </Box>
                                  <Box>
                                    <Text fontSize="xs" color="gray.600">
                                      Premium
                                    </Text>
                                    <Text fontSize="xs">{premium}</Text>
                                  </Box>

                                  {p.type === "placing" && (
                                    <Box>
                                      <Text fontSize="xs" color="gray.600">
                                        Placing Key
                                      </Text>
                                      <Text fontSize="xs">{s.placingKey}</Text>
                                    </Box>
                                  )}

                                  <Box>
                                    <Text fontSize="xs" color="gray.600">
                                      Accounting Key
                                    </Text>
                                    <Text fontSize="xs">{s.accountingKey}</Text>
                                  </Box>
                                </SimpleGrid>
                              </Box>

                              {p.type === "placing" && (
                                <FactorValueListTable
                                  key="placing"
                                  title="Applicable Placing Factors"
                                  items={
                                    Array.isArray(s.placingFactorValueIds)
                                      ? s.placingFactorValueIds
                                      : []
                                  }
                                />
                              )}

                              <FactorValueListTable
                                key="accounting"
                                title="Applicable Accounting Factors"
                                items={
                                  Array.isArray(s.accountingFactorValueIds)
                                    ? s.accountingFactorValueIds
                                    : []
                                }
                              />
                            </SimpleGrid>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
};

// Build maps so we can resolve factor/value labels by id when rendering segments
const buildLookups = (panner) => {
  const factorById = new Map();
  const valueById = new Map();

  const add = (arr = []) => {
    arr.forEach((f) => {
      if (f?.id) factorById.set(f.id, f);
      (f?.values || []).forEach((v) => {
        if (v?.id) valueById.set(v.id, v);
      });
    });
  };

  add(panner?.placingFactors);
  add(panner?.accountingFactors);

  return {
    factorLabel: (id) => {
      const f = factorById.get(id);
      return f?.type || f?.name || id || "";
      // tweak if you prefer name over type
    },
    valueLabel: (id) => {
      const v = valueById.get(id);
      return v?.code || v?.description || id || "";
    },
  };
};

/** Right pane main form */
const PannerForm = ({
  panner,
  setPanner,
  onSave,
  onGenerate,
  onMapfaculate,
}) => {
  const set = (patch) => setPanner({ ...panner, ...patch });
  const projections = panner?.projections || [];

    const appendToAccounting = (copiedFactor) => {
    const current = panner?.accountingFactors || [];
    const next = [
      ...current,
      { ...copiedFactor, order: current.length + 1 }, // assign order here
    ];
    set({ accountingFactors: next });
  };

  return (
    <Stack spacing={4}>
      {/* Header / actions */}
      <Box
        borderWidth="1px"
        borderRadius="md"
        p={1}
        backgroundColor="whiteAlpha.800">
        <FormControl>
          <Input
            size="md"
            fontWeight="bold"
            value={panner?.name || ""}
            onChange={(e) => set({ name: e.target.value })}
          />
        </FormControl>
        <HStack mt={3} spacing={2}>
          <Button
            leftIcon={<HiOutlineSave />}
            colorScheme="linkedin"
            onClick={onSave}>
            Save
          </Button>
          <Button
            variant="outline"
            leftIcon={<HiOutlinePlay />}
            onClick={onGenerate}>
            Generate combinations
          </Button>
        </HStack>
        <SimpleGrid columns={[1, 3]} spacing={3} mt={3}>
          <FormControl>
            <FormLabel fontSize="xs" color="gray.600">Premium</FormLabel>
            <NumberInput
              size="xs"
              min={0}
              precision={2}
              value={panner?.premium ?? 0}
              onChange={(_, n) => set({ premium: n })}>
              <NumberInputField />
            </NumberInput>
          </FormControl>
        </SimpleGrid>
      </Box>

      {/* Side-by-side factor panels */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} alignItems="start">
        <Box>
          <FactorEditor
            title="Placing Factors"
            factors={panner?.placingFactors || []}
            setFactors={(f) => set({ placingFactors: f })}
            onCopyToAccounting={appendToAccounting}
            premium={panner?.premium || 0}
          />
        </Box>
        <Box>
          <FactorEditor
            title="Accounting Factors"
            factors={panner?.accountingFactors || []}
            setFactors={(f) => set({ accountingFactors: f })}
            premium={panner?.premium || 0}
          />
        </Box>
      </SimpleGrid>

      {/* Projections (tabs) */}
      <ProjectionsTabs
        projections={panner?.projections || []}
        onMapfaculate={onMapfaculate}
        lookup={buildLookups(panner)}
      />
    </Stack>
  );
};

/* -----------------------
   Main Component
------------------------*/
const Panner = () => {
  const [panners, setPanners] = useState([]);
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [panner, setPanner] = useState(null);
  const [loading, setLoading] = useState(false);

  const toast = useToast();
  const { user } = useAuth();

  const api = `https://panner.poc.mrc.ai/api`;

  const refreshHeaders = async () => {
    try {
      const res = await fetch(`${api}/panners/headers`);
      const data = await res.json();
      setPanners(Array.isArray(data) ? data : []);
    } catch {
      toast({ title: "Error loading panners", status: "error" });
    }
  };

  const loadPanner = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${api}/panners/${id}`);
      if (!res.ok) throw await errorFromResponse(res);
      const data = await res.json();
      setPanner(data);
      setSelectedId(id);
    } catch (e) {
      toast({
        title: "Error loading panner",
        description: String(e.message || e),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const createPanner = async () => {
    try {
      const body = {
        name: `New Panner ${new Date().toLocaleString()}`,
        premium: 0,
        placingFactors: [],
        accountingFactors: [],
        projections: [],
        createdDate: new Date().toISOString(),
      };
      const res = await fetch(`${api}/panners`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withOwnerOrganisation(body, user)),
      });
      if (!res.ok) throw await errorFromResponse(res);
      const data = await res.json();
      toast({ title: "Panner created", status: "success" });
      await refreshHeaders();
      await loadPanner(data._id);
    } catch (e) {
      toast({
        title: "Error creating panner",
        description: String(e.message || e),
        status: "error",
      });
    }
  };

  const savePanner = async () => {
    if (!panner?._id) return;
    try {
      const payload = shapePannerForApi(panner);
      const res = await fetch(`${api}/panners/${panner._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await errorFromResponse(res);
      const data = await res.json();
      setPanner(data);
      toast({ title: "Saved", status: "success" });

      // optimistic header update so the rail shows the latest name
      setPanners((prev) =>
        prev.map((h) =>
          h._id === payload._id ? { ...h, name: payload.name } : h
        )
      );

      await refreshHeaders();
    } catch (e) {
      toast({
        title: "Save failed",
        description: String(e.message || e),
        status: "error",
      });
    }
  };

  const generate = async () => {
    if (!panner?._id) return;
    try {
      const payload = shapePannerForApi(panner);
      const res = await fetch(`${api}/panners/${panner._id}/project`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await errorFromResponse(res);
      const data = await res.json();
      setPanner(data);
      toast({ title: "Combinations generated", status: "success" });
    } catch (e) {
      toast({
        title: "Generate failed",
        description: String(e.message || e),
        status: "error",
      });
    }
  };

  const mapfaculate = async (projectionId) => {
    if (!panner?._id || !projectionId) return;
    try {
      const payload = shapePannerForApi(panner);
      console.log("Mapping & allocating...");
      console.log("=======================");
      console.log(
        "API endpoint",
        `${api}/panners/${panner._id}/mapfaculate/${projectionId}`
      );
      const res = await fetch(
        `${api}/panners/${panner._id}/mapfaculate/${encodeURIComponent(
          projectionId
        )}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw await errorFromResponse(res);
      const data = await res.json();
      setPanner(data);
      toast({ title: "Mapped & allocated", status: "success" });
    } catch (e) {
      toast({
        title: "Map/allocate failed",
        description: String(e.message || e),
        status: "error",
      });
    }
  };

  useEffect(() => {
    refreshHeaders();
  }, []);

  const filtered = useMemo(() => {
    const t = (filter || "").toLowerCase();
    return (panners || []).filter((x) =>
      (x.name || "").toLowerCase().includes(t)
    );
  }, [panners, filter]);

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />
        <Box position="relative" zIndex={1} p={1}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={1}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <HStack spacing={3} align="center">
              <Heading size="lg">Panner</Heading>
              <Tooltip label="Refresh">
                <IconButton
                  aria-label="Refresh"
                  icon={<HiOutlineRefresh />}
                  size="sm"
                  onClick={refreshHeaders}
                />
              </Tooltip>
              <Button
                size="sm"
                leftIcon={<HiOutlinePlus />}
                onClick={createPanner}>
                New Panner
              </Button>
            </HStack>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={["1fr", "280px 1fr"]}
            gap={3}
            alignItems="start">
            {/* Left: slim rail */}
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={1}
              backgroundColor="whiteAlpha.800"
              maxH="80vh"
              overflow="hidden">
              <InputGroup size="sm" mb={1}>
                <InputLeftElement pointerEvents="none">
                  <HiOutlineSearch />
                </InputLeftElement>
                <Input
                  placeholder="Search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </InputGroup>

              <Box overflowY="auto" maxH="calc(80vh - 3rem)">
                {filtered.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" px={1}>
                    No panners
                  </Text>
                ) : (
                  <Stack spacing={1}>
                    {filtered.map((h) => (
                      <HStack
                        key={h._id}
                        spacing={2}
                        p={1}
                        borderWidth="1px"
                        borderRadius="md"
                        cursor="pointer"
                        bg={selectedId === h._id ? "blue.50" : "white"}
                        _hover={{
                          bg: selectedId === h._id ? "blue.100" : "gray.50",
                        }}
                        onClick={() => loadPanner(h._id)}
                        align="center">
                        <Box flex="1" minW={0}>

                          <Tooltip label={h._id}>
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            noOfLines={1}
                            title={h.name}>
                            {h.name || "(unnamed)"}
                          </Text>
                          </Tooltip>
                        </Box>
                        <Tooltip label="Load">
                          <IconButton
                            aria-label="Load"
                            icon={<HiOutlineUpload />}
                            size="xs"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              loadPanner(h._id);
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Right: main form */}
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={1}
              backgroundColor="whiteAlpha.800"
              minH="80vh">
              {!panner ? (
                <Box opacity={0.8} fontStyle="italic">
                  Select a panner from the left or create a new one.
                </Box>
              ) : (
                <PannerForm
                  panner={panner}
                  setPanner={setPanner}
                  onSave={savePanner}
                  onGenerate={generate}
                  onMapfaculate={mapfaculate}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Panner;
